import { useEffect, useState } from 'react';
import { Box, alpha, useTheme } from '@mui/material';
import { keyframes } from '@emotion/react';
import { getCellIcon } from './utils';
import { TDataTableGridIcons } from './constants';

type TIconCellProps = {
  row: any;
  icons: TDataTableGridIcons | undefined;
};

// Renders the trending widget's trending icon.
const IconCell = ({ row, icons }: TIconCellProps) => {
  const theme = useTheme();
  const [state, setState] = useState<any>({
    iconKey: row?.value,
    iconStylesAndTitle: icons?.list[row?.value],
    iconColorCode: '',
    iconDarkBgColor: '',
    iconLightBgColor: '',
    pingAnimation: '',
    isVisible: true,
    hasAnimationEnabled: false,
  });

  useEffect(() => {
    const iconKey = row?.value;
    const iconStylesAndTitle = icons?.list[iconKey];
    const isVisible: boolean = !!iconKey?.length;
    const hasAnimationEnabled: boolean = iconStylesAndTitle?.animation ?? false;

    if (isVisible) {
      const iconStyleVariant =
        (iconStylesAndTitle?.variant as keyof typeof theme.blox.table) ?? 'neutral';
      const isNeutralVariant = iconStyleVariant === 'neutral';
      const iconColorCode = isNeutralVariant
        ? theme?.blox?.table?.text
        : theme?.blox?.table?.[iconStyleVariant]; // Ex. #ff0000
      const iconDarkBgColor = isNeutralVariant ? 'none' : alpha(iconColorCode, 0.32);
      const iconLightBgColor = isNeutralVariant ? 'none' : alpha(iconColorCode, 0.06);
      const pingAnimation = keyframes`
      0% {
        backgroundColor: ${iconDarkBgColor};
        box-shadow: 0 0 0 0px ${iconDarkBgColor};
      }
      100% {
        backgroundColor: ${iconLightBgColor};
        box-shadow: 0 0 0 10px ${iconLightBgColor};
      }
    `;

      setState((prevState: any) => ({
        ...prevState,
        iconKey,
        iconStylesAndTitle,
        iconColorCode,
        iconDarkBgColor,
        iconLightBgColor,
        isVisible,
        hasAnimationEnabled,
        pingAnimation,
      }));
    }
  }, [row, icons, theme?.blox?.table, theme]);

  return (
    <Box
      data-testid={`status-icon-${state.iconKey}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        maxWidth: '36px',
        height: '100%',
        backgroundColor: state.isVisible ? state.iconDarkBgColor : 'none',
        borderRadius: '50%',
        boxShadow: `0px 0px 1px 1px ${state.iconLightBgColor}`,
        animation: !!state.hasAnimationEnabled
          ? `${state.pingAnimation} 1s cubic-bezier(0,0,.2,1) infinite`
          : 'none',

        '& .MuiSvgIcon-root': {
          ...(state.isVisible && {
            fill: state.iconColorCode,
          }),
        },
      }}
    >
      {getCellIcon(state.iconStylesAndTitle?.icon)}
    </Box>
  );
};

export default IconCell;
